import { IStorageExpiry } from '@kurtosys/types/helpers/StorageHelper/IStorageExpiry.js';
import { IStorageHelperConfigurationOptions } from '@kurtosys/types/helpers/StorageHelper/IStorageHelperConfigurationOptions.js';
import { IStorageOption } from '@kurtosys/types/helpers/StorageHelper/IStorageOption.js';
import { IStorageOptions } from '@kurtosys/types/helpers/StorageHelper/IStorageOptions.js';
import { TStorageType } from '@kurtosys/types/helpers/StorageHelper/TStorageType.js';
import dayjs from 'dayjs';

import { jsonTryParse } from '../commonUtils/json.js';
import { deObfuscate, isObfuscated, obfuscate } from '../commonUtils/obfuscation.js';

export class StorageHelper {
	public type: TStorageType;
	private obfuscated?: boolean = false;
	private allowMultiple?: boolean = false;
	private expiry?: IStorageExpiry;
	private getUserConfiguration?: (key: string) => Promise<object | null>;
	private setUserConfiguration?: (key: string, value: string) => Promise<boolean>;
	private removeUserConfiguration?: (key: string) => Promise<boolean>;

	constructor(storageHelperOptions: IStorageHelperConfigurationOptions) {
		this.type = storageHelperOptions.type;
		if (storageHelperOptions.obfuscated !== undefined) {
			this.obfuscated = storageHelperOptions.obfuscated;
		}
		if (storageHelperOptions.allowMultiple) {
			this.allowMultiple = storageHelperOptions.allowMultiple;
		}
		if (storageHelperOptions.expiry) {
			this.expiry = storageHelperOptions.expiry;
		}
		this.getUserConfiguration = storageHelperOptions.getUserConfiguration;
		this.setUserConfiguration = storageHelperOptions.setUserConfiguration;
		this.removeUserConfiguration = storageHelperOptions.removeUserConfiguration;
	}

	async getStorageValues(key: string): Promise<object | undefined> {
		if (this.type === 'DB') {
			return await this.getUserConfiguration(key);
		}
		let storageString: string;
		if (this.type === 'COOKIE') {
			const cookies = document.cookie.split('; ');
			const cookie = cookies.find((c) => c.startsWith(`${key}=`));
			if (cookie) {
				// Remove the cookie key from the string to
				storageString = cookie.replace(`${key}=`, '');
			}
		} else {
			// Get the local or session storage
			const options = this.getStorageOptions();
			storageString = options?.target.storage.getItem(key);
		}
		if (storageString) {
			if (isObfuscated(storageString)) {
				storageString = deObfuscate(storageString);
			}
			const storageConversion = jsonTryParse(storageString);
			if (storageConversion.isValid) {
				return Promise.resolve(storageConversion.value);
			}
		}
		return Promise.resolve(undefined);
	}

	async setStorageValues(key: string, value: any): Promise<boolean> {
		if (this.type === 'DB') {
			return this.setUserConfiguration(key, value);
		}
		let storageValue = JSON.stringify(value);
		if (this.obfuscated) {
			storageValue = obfuscate(storageValue);
		}
		if (this.type === 'COOKIE') {
			const expires = this.getExpiryDate();
			const cookieString = `${key}=${storageValue}; SameSite=Lax; expires=${expires}`;
			document.cookie = cookieString;
			return Promise.resolve(true);
		}
		// Get the local or session storage
		const options = this.getStorageOptions();
		if (options) {
			if (!this.allowMultiple) {
				options.clear.storage.removeItem(key);
			}
			options.target.storage.setItem(key, storageValue);
			return Promise.resolve(true);
		}
		return Promise.resolve(false);
	}

	async removeStorageValues(key: string): Promise<boolean> {
		if (this.type === 'DB') {
			return this.removeUserConfiguration(key);
		}
		if (this.type === 'COOKIE') {
			const cookies = document.cookie.split('; ');
			// Filter out the key value
			const cookie = cookies.filter((c) => !c.startsWith(`${key}=`));
			document.cookie = cookie.join('; ');
			return Promise.resolve(true);
		}
		const options = this.getStorageOptions();
		if (options) {
			options.target.storage.removeItem(key);
			options.clear.storage.removeItem(key);
			return Promise.resolve(true);
		}
		return Promise.resolve(false);
	}

	private getStorageOptions(): IStorageOptions | undefined {
		let options: IStorageOptions | undefined;
		// Have to access the storage from the window object, for the sake of the tests
		const localStorageOption: IStorageOption = {
			storage: window.localStorage,
		};
		const sessionStorageOption: IStorageOption = {
			storage: window.sessionStorage,
		};
		switch (this.type) {
			case 'LOCAL':
				options = {
					target: localStorageOption,
					clear: sessionStorageOption,
				};
				break;
			case 'SESSION':
				options = {
					target: sessionStorageOption,
					clear: localStorageOption,
				};
				break;
		}
		return options;
	}

	public getExpiryDate(): string | undefined {
		if (this.expiry) {
			const { period, value } = this.expiry;
			const currentDate = dayjs(new Date());
			let expiryDate: dayjs.Dayjs;
			switch (period) {
				case 'SECONDS':
					expiryDate = currentDate.add(value, 'second');
					break;
				case 'MINUTES':
					expiryDate = currentDate.add(value, 'minute');
					break;
				case 'HOURS':
					expiryDate = currentDate.add(value, 'hour');
					break;
				case 'DAYS':
					expiryDate = currentDate.add(value, 'day');
					break;
				case 'WEEKS':
					expiryDate = currentDate.add(value, 'week');
					break;
				case 'MONTHS':
					expiryDate = currentDate.add(value, 'month');
					break;
				case 'YEARS':
					expiryDate = currentDate.add(value, 'year');
					break;
			}
			if (expiryDate) {
				return expiryDate.toDate().toUTCString();
			}
		}
	}
}
